import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { environment } from "../../../environments/environment";
import { AuthGuard } from "../../core/guards/auth.guard";
import { AppRoutes } from "../../shared/types/router.type";
import { AssetBulkUploadComponent } from "./assets/asset-bulk-upload/asset-bulk-upload.component";
import { AssetsComponent } from "./assets/assets.component";
import { CreateAssetComponent } from "./assets/create-asset/create-asset.component";
import { ManageAssetComponent } from "./assets/manage-asset/manage-asset.component";
import { CompaniesComponent } from "./companies/companies.component";
import { CompanyBulkUploadComponent } from "./companies/company-bulk-upload/company-bulk-upload.component";
import { CreateCompanyComponent } from "./companies/create-company/create-company.component";
import { ManageCompanyComponent } from "./companies/manage-company/manage-company.component";
import { CompanyAttachmentComponent } from "./company-attachments/company-attachment.component";
import { ConfigurableOptionComponent } from "./configurable-options/configurable-options.component";
import { CreateConfigurableOptionComponent } from "./configurable-options/create-configurable-option/create-configurable-option.component";
import { ManageConfigurableOptionComponent } from "./configurable-options/manage-configurable-option/manage-configurable-option.component";
import { ContractsComponent } from "./contracts/contract.component";
import { CreateContractComponent } from "./contracts/create-contract/create-contract.component";
import { ManageContractComponent } from "./contracts/manage-contract/manage-contract.component";
import { DashboardComponent } from "./dashboard/dashboard.component";
import { CreateInternalProviderComponent } from "./internal-provider/create-internal-provider/create-internal-provider.component";
import { InternalProviderComponent } from "./internal-provider/internal-provider.component";
import { ManageInternalProviderComponent } from "./internal-provider/manage-internal-provider/manage-internal-provider.component";
import { CreateLocationsComponent } from "./locations/create-locations/create-locations.component";
import { LocationBulkUploadComponent } from "./locations/location-bulk-upload/location-bulk-upload.component";
import { LocationsComponent } from "./locations/locations.component";
import { ManageLocationComponent } from "./locations/manage-location/manage-location.component";
import { CreateMaintenanceProviderComponent } from "./maintenance-provider/create-maintenance-provider/create-maintenance-provider.component";
import { MaintenanceProviderComponent } from "./maintenance-provider/maintenance-provider.component";
import { ManageMaintenanceProviderComponent } from "./maintenance-provider/manage-maintenance-provider/manage-maintenance-provider.component";
import { CreatePartnerComponent } from "./partners/create-partner/create-partner.component";
import { ManagePartnerComponent } from "./partners/manage-partner/manage-partner.component";
import { PartnerBulkUploadComponent } from "./partners/partner-bulk-upload/partner-bulk-upload.component";
import { PartnersComponent } from "./partners/partner.component";
import { PortalComponent } from "./portal.component";
import { ChangePasswordComponent } from "./profile/change-password/change-password.component";
import { ProfileComponent } from "./profile/profile.component";
import { CreateQuoteComponent } from "./quotes/create-quote/create-quote.component";
import { ManageQuoteComponent } from "./quotes/manage-quote/manage-quote.component";
import { QuotesComponent } from "./quotes/quote.component";
import { ReportingComponent } from "./reporting/reporting.component";
import { CreateTicketComponent } from "./tickets/create-ticket/create-ticket.component";
import { ManageTicketsComponent } from "./tickets/manage-tickets/manage-tickets.component";
import { TicketsComponent } from "./tickets/tickets.component";
import { ActivateUserComponent } from "./users/activate-user/activate-user.component";
import { CreateUserComponent } from "./users/create-user/create-user.component";
import { ManageUserComponent } from "./users/manage-user/manage-user.component";
import { UsersComponent } from "./users/users.component";

const routes: AppRoutes = [
  {
    path: "",
    component: PortalComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: "dashboard",
        component: DashboardComponent,
      },
      // USERS
      {
        path: "users",
        component: UsersComponent,
      },
      {
        path: "users/create",
        component: CreateUserComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [
            { types: ["global"], rights: ["admin", "manager"] },
            { types: ["partner"], rights: ["admin", "manager"] },
            { types: ["company"], rights: ["admin"] },
          ],
        },
      },
      {
        path: "users/:id",
        component: ManageUserComponent,
      },
      {
        path: "users/:id/activate",
        component: ActivateUserComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["admin"] }],
        },
      },
      {
        path: "profile",
        component: ProfileComponent,
      },
      {
        path: "profile/change-password",
        component: ChangePasswordComponent,
      },
      // Partners
      {
        path: "partners",
        component: PartnersComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["global"], rights: ["*"] }] },
      },
      {
        path: "partners/create",
        component: CreatePartnerComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["global"], rights: ["admin", "manager"] }] },
      },
      {
        path: "partners/bulk-upload",
        component: PartnerBulkUploadComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["global"], rights: ["admin", "manager"] }] },
      },

      {
        path: "partners/:id",
        component: ManagePartnerComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["global"], rights: ["*"] }] },
      },
      // ASSETS
      {
        path: "assets",
        component: AssetsComponent,
      },
      {
        path: "assets/bulk-upload",
        component: AssetBulkUploadComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["*"], rights: ["admin", "manager"] }] },
      },
      {
        path: "assets/create",
        component: CreateAssetComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["*"], rights: ["admin", "manager"] }] },
      },
      {
        path: "assets/:id",
        component: ManageAssetComponent,
      },
      // COMPANIES
      {
        path: "companies",
        component: CompaniesComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["global", "partner"], rights: ["*"] }] },
      },
      {
        path: "companies/create",
        component: CreateCompanyComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global", "partner"], rights: ["admin", "manager"] }],
        },
      },
      {
        path: "companies/bulk-upload",
        component: CompanyBulkUploadComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global", "partner"], rights: ["admin", "manager"] }],
        },
      },
      {
        path: "companies/:id",
        component: ManageCompanyComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["global", "partner"], rights: ["*"] }] },
      },

      {
        path: "company-attachments",
        component: CompanyAttachmentComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["company"], rights: ["*"] }] },
      },
      // CONTRACTS
      {
        path: "contracts",
        component: ContractsComponent,
      },
      {
        path: "contracts/create",
        component: CreateContractComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["*"], rights: ["admin", "manager"] }] },
      },
      {
        path: "contracts/:id",
        component: ManageContractComponent,
      },
      // Quotes
      {
        path: "quotes",
        component: QuotesComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "quotes/create",
        component: CreateQuoteComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "quotes/:id",
        component: ManageQuoteComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      // locations
      {
        path: "locations",
        component: LocationsComponent,
      },
      {
        path: "locations/create",
        component: CreateLocationsComponent,
      },
      {
        path: "locations/bulk-upload",
        component: LocationBulkUploadComponent,
        canActivate: [AuthGuard],
        data: { allowed: [{ types: ["*"], rights: ["admin", "manager"] }] },
      },
      {
        path: "locations/:id",
        component: ManageLocationComponent,
      },

      // Tickets
      {
        path: "tickets",
        component: TicketsComponent,
      },
      {
        path: "tickets/create",
        component: CreateTicketComponent,
        canActivate: [AuthGuard],
      },
      // Config
      {
        path: "maintenance-providers",
        component: MaintenanceProviderComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "maintenance-providers/create",
        component: CreateMaintenanceProviderComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["admin"] }],
        },
      },
      {
        path: "maintenance-providers/:id",
        component: ManageMaintenanceProviderComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      // Internal Providers
      {
        path: "internal-providers",
        component: InternalProviderComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "internal-providers/create",
        component: CreateInternalProviderComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["admin"] }],
        },
      },
      {
        path: "internal-providers/:id",
        component: ManageInternalProviderComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "tickets/:id",
        component: ManageTicketsComponent,
      },
      // Reporting
      {
        path: "reporting",
        component: ReportingComponent,
      },
      // Configurable Options
      {
        path: "configurable-options",
        component: ConfigurableOptionComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "configurable-options/create",
        component: CreateConfigurableOptionComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
      {
        path: "configurable-options/:id",
        component: ManageConfigurableOptionComponent,
        canActivate: [AuthGuard],
        data: {
          allowed: [{ types: ["global"], rights: ["*"] }],
        },
      },
    ].filter((route) => !environment.disabledPaths.some((disabledPath) => route.path.includes(disabledPath))) as Routes,
  },
];
@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class PortalRoutingModule {}
